import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import SearchWidget from "@/components/SearchWidget";
import PropertyCard from "@/components/PropertyCard";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { ecCheck } from "@/lib/easyConcierge";

const FILTERS = [
  { k: "", label: "Tous" },
  { k: "voyageurs", label: "Voyageurs" },
  { k: "entreprises", label: "Entreprises" },
];

export default function PropertyList() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const guests = params.get("guests");
  const aud = params.get("public") || "";
  const checkin = params.get("checkin");
  const checkout = params.get("checkout");

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (guests) qs.set("guests", guests);
    if (aud) qs.set("audience", aud);
    const q = qs.toString() ? `?${qs.toString()}` : "";
    api.get(`/properties${q}`).then(async (r) => {
      let list = r.data;
      if (checkin && checkout) {
        const checks = await Promise.all(list.map((p) => (p.easyconcierge_id
          ? ecCheck(p.easyconcierge_id, checkin, checkout).then((d) => !(d.success && d.available === false)).catch(() => true)
          : true)));
        list = list.filter((_, i) => checks[i]);
      }
      setProps(list);
    }).finally(() => setLoading(false));
  }, [guests, aud, checkin, checkout]);

  const setFilter = (k) => {
    const next = new URLSearchParams(params);
    if (k) next.set("public", k); else next.delete("public");
    setParams(next);
  };

  return (
    <Layout>
      <Seo title="Nos logements à Rouen | Location courte durée | NJ Conciergerie"
        description="Découvrez nos appartements et studios en location courte durée à Rouen. Réservation directe et sécurisée, séjours confortables et bien équipés."
        canonical="/logements" jsonLd={useLocalBusinessLd()} />
      <div className="bg-nj-olive/40 border-b border-nj-olive">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="font-serif font-light text-3xl sm:text-4xl text-nj-text mb-6">{t("home.propertiesTitle")}</h1>
          <SearchWidget compact />
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="audience-filter">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k || "all"}`}
              className={`text-sm font-medium px-4 py-2 rounded-sm border transition-colors ${
                aud === f.k ? "bg-nj-primary text-white border-nj-primary" : "border-nj-olive text-nj-text2 hover:bg-nj-olive"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {loading ? (
          <p className="text-nj-muted">{t("common.loading")}</p>
        ) : props.length === 0 ? (
          <p className="text-nj-muted">Aucun logement ne correspond à votre recherche.</p>
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="property-list">
            {props.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
